import { useEffect, useState } from 'react';
import { formatPrice, type Position, type Squad, type SquadPlayer, type SquadValidation } from '@fantasy/types';
import type { UnmatchedScreenshotPlayer } from '../api.ts';
import { AnalysisChat } from '../components/AnalysisChat.tsx';
import { AnalysisView } from '../components/AnalysisView.tsx';
import { ChipCalendar } from '../components/ChipCalendar.tsx';
import { EmailPreferences } from '../components/EmailPreferences.tsx';
import { ImportSquad } from '../components/ImportSquad.tsx';
import { LinkFplTeam } from '../components/LinkFplTeam.tsx';
import { SquadEditor } from '../components/SquadEditor.tsx';
import { SquadHistory } from '../components/SquadHistory.tsx';
import { SquadLibrary } from '../components/SquadLibrary.tsx';
import { SquadView } from '../components/SquadView.tsx';
import { TransferPlanView } from '../components/TransferPlanView.tsx';
import { useSquad } from '../state/useSquad.ts';

/** The horizons offered by the transfer-plan control — 3 is the server's own
 * default (see `PlanRequest`), 5 and 8 cover "a bit further" and "the max
 * the search will run". */
const PLAN_HORIZONS = [3, 5, 8] as const;

/** Groups a flat player list into SquadEditor's per-position slot shape. Used
 * both for a complete squad (every slot filled) and a partial import (only
 * the matched positions filled — SquadEditor pads the rest with empty
 * pickers on its own). */
function groupByPosition(players: SquadPlayer[]): Partial<Record<Position, SquadPlayer[]>> {
  const grouped: Partial<Record<Position, SquadPlayer[]>> = {};
  for (const player of players) {
    (grouped[player.position] ??= []).push(player);
  }
  return grouped;
}

/** The squad desk — the app's default route (T-048). Everything here used to
 * be `App`'s own `SquadDesk`; the fixture grid and tracker that used to sit
 * above and below it now have their own routes (`fixturesRoute.tsx`,
 * `trackerRoute.tsx`) and no longer render here. */
export function SquadRoute() {
  const {
    squad, name, gameweek, hasSavedSquads, editorPlayers, unmatched, editOpen, phase, problems,
    validation, analysis, plan, planning, planProblems, planHorizon, planAllowHits,
    chipPlan, chipPlanning, chipPlanProblems,
    libraryRefresh, previousSquad,
    setEditOpen, setEditorPlayers, setUnmatched, setPlanHorizon, setPlanAllowHits,
    saveSquad, importSquad, loadFromLibrary, returnToPreviousSquad,
    analyseSquad, requestPlan, requestChipPlan,
  } = useSquad();

  // SquadEditor's bank/free-transfers fields, derived from the loaded squad
  // rather than tracked separately — `bank` converts tenths to millions.
  const bank = squad ? squad.bank / 10 : 0.5;
  const freeTransfers = squad ? squad.freeTransfers : 1;

  // Bumped on every library load (and on returning to the previous squad),
  // so SquadView knows to fetch that gameweek's points immediately rather
  // than waiting for a manual click, and so the page scrolls back up to
  // show it — both make it obvious a different squad just loaded in, not
  // just a silent state change below the fold.
  const [squadFocusSignal, setSquadFocusSignal] = useState(0);

  // Skipped on the very first render — nothing has scrolled yet, and the
  // squad view might not even exist in the DOM (no squad restored yet).
  useEffect(() => {
    if (squadFocusSignal === 0) return;
    document.getElementById('squad-focus-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [squadFocusSignal]);

  // Screenshot import is the primary, front-and-centre action for a manager
  // who has never brought a squad in — but it's normally a one-off: once a
  // squad exists (this session, or restored from a past visit — see
  // `hasSavedSquads` above), re-importing is rare, not the first thing worth
  // seeing. See where this is rendered further down.
  const hasSquad = squad !== null || hasSavedSquads;

  /** Bumps the focus signal on top of the context's own `loadFromLibrary`,
   * so the page scrolls to the squad view and its live points load. */
  function onLoadFromLibrary(loaded: Squad, squadValidation: SquadValidation) {
    loadFromLibrary(loaded, squadValidation);
    setSquadFocusSignal((n) => n + 1);
  }

  /** Same as above — "Back to {name}" is also a squad switch. */
  function onReturnToPreviousSquad() {
    if (!previousSquad) return;
    returnToPreviousSquad();
    setSquadFocusSignal((n) => n + 1);
  }

  /** A screenshot import that couldn't match everyone — open the editor with
   * matched players already placed in their real position slots. There's no
   * single position to guess an unmatched name into, so every other slot is
   * left empty for the user to search and fill themselves; the unmatched
   * names are surfaced as a banner instead. */
  function onPartialImport(matched: SquadPlayer[], unmatchedPlayers: UnmatchedScreenshotPlayer[]) {
    setEditorPlayers(groupByPosition(matched));
    setUnmatched(unmatchedPlayers);
    setEditOpen(true);
  }

  /** The squad view's one "Edit my squad" button — pre-fills every slot with
   * the squad exactly as it stands now, since a complete squad always has the
   * right number of players in each position already. */
  function onEditRequested() {
    if (!squad) return;
    setEditorPlayers(groupByPosition(squad.players));
    setUnmatched([]);
    setEditOpen(true);
  }

  // Rendered at one of two positions below depending on hasSquad, not both —
  // defined once here so its props stay a single source of truth.
  const importSquadPanel = (
    <ImportSquad
      gameweek={gameweek}
      onImported={importSquad}
      onPartialImport={onPartialImport}
      defaultOpen={!hasSquad}
    />
  );

  return (
    <>
      {!hasSquad && (
        <>
          <section className="hero" aria-labelledby="hero-heading">
            <h2 id="hero-heading">
              Screenshot in. <em>Verdict out.</em>
            </h2>
            <p>
              Upload your "Pick Team" screen. We match it against live prices, form and fixtures,
              and tell you the armband, the transfer worth making, and the one that isn't —
              reasoning shown, not just a score.
            </p>
          </section>

          {importSquadPanel}
        </>
      )}

      {/* Editing replaces the squad view in place rather than appending
       * below it (a long form off-screen below a tall pitch view is easy
       * to miss). Shared scroll anchor with the "viewing a different
       * squad" banner below, so scrolling to the squad view's own heading
       * can't push that banner off the top of the screen. */}
      {squad && !editOpen && (
        <div id="squad-focus-anchor">
          {previousSquad && (
            <section className="panel panel--accent-2" aria-label="Viewing a squad loaded from Saved squads">
              <p className="hint">
                Viewing "{squad.name}" (Gameweek {squad.gameweek}) from Saved squads.
              </p>
              <button type="button" className="ghost small" onClick={onReturnToPreviousSquad}>
                Back to {previousSquad.squad.name}
              </button>
            </section>
          )}

          <LinkFplTeam />

          <SquadView
            squad={squad}
            validation={validation}
            onAnalyse={(options) => analyseSquad(squad.id, options)}
            onEdit={onEditRequested}
            analysing={phase === 'working'}
            fetchLivePointsSignal={squadFocusSignal}
          />

          {/* Renders nothing for a squad with no FPL entry id — see the
           * component's own comment (T-129). */}
          <SquadHistory squadId={squad.id} entryId={squad.entryId} currentGameweek={squad.gameweek} />
        </div>
      )}

      {editOpen && (
        <>
          {unmatched.length > 0 && (
            <section className="panel panel--accent-2" aria-label="Unmatched players">
              <p className="hint">
                {unmatched.length === 1
                  ? "1 player couldn't be matched — find them below and add them to the right slot."
                  : `${unmatched.length} players couldn't be matched — find them below and add them to the right slot.`}
              </p>
              <ul className="notices">
                {unmatched.map((player) => (
                  <li key={`${player.nameFragment}-${player.club}`} className="error">
                    Couldn't match: {player.nameFragment} ({player.club})
                  </li>
                ))}
              </ul>
            </section>
          )}

          <SquadEditor
            initialPlayers={editorPlayers}
            initialMeta={{ name, gameweek, bank, freeTransfers }}
            onSave={saveSquad}
            onCancel={() => setEditOpen(false)}
            saving={phase === 'working'}
          />

          {problems.length > 0 && (
            <ul className="notices mt-xl">
              {problems.map((problem) => (
                <li key={problem} className="error">
                  {problem}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {validation && !validation.valid && (
        <section className="panel panel--bad" aria-label="Squad rule violations">
          <h3>That squad is not legal</h3>
          <p className="hint">
            Total value {formatPrice(validation.totalValue)}. Fix these before acting on any advice.
          </p>
          <ul className="notices">
            {validation.violations.map((violation) => (
              <li key={violation.message} className="error">
                {violation.message}
              </li>
            ))}
          </ul>
        </section>
      )}

      {analysis && <AnalysisView analysis={analysis} />}

      {/* Reads squad/analysis off the context itself and renders nothing
       * until a verdict exists — see AnalysisChat's own comment. */}
      <AnalysisChat />

      {squad && !editOpen && (
        <section className="panel" aria-label="Multi-week transfer plan">
          <div className="panel-head">
            <div>
              <h3>Transfer plan</h3>
              <p className="hint">
                What to do this week, and what's worth banking a few gameweeks out.
              </p>
            </div>
          </div>
          <div className="row">
            <div className="field field--grow">
              <label id="plan-horizon-label">Look ahead (gameweeks)</label>
              <div className="row row--flush" role="group" aria-labelledby="plan-horizon-label">
                {PLAN_HORIZONS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className="ghost small"
                    aria-pressed={planHorizon === value}
                    onClick={() => setPlanHorizon(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={planAllowHits}
                onChange={(e) => setPlanAllowHits(e.target.checked)}
              />
              Allow hits
            </label>
            <button
              type="button"
              className="small"
              onClick={() => requestPlan(squad.id)}
              disabled={planning}
              aria-busy={planning}
            >
              {planning && <span className="spinner" aria-hidden="true" />}
              {planning ? 'Planning…' : 'Plan my transfers'}
            </button>
          </div>

          {planProblems.length > 0 && (
            <ul className="notices mt-md">
              {planProblems.map((problem) => (
                <li key={problem} className="error">
                  {problem}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {plan && <TransferPlanView plan={plan} />}

      {squad && !editOpen && (
        <section className="panel" aria-label="Chip calendar">
          <div className="panel-head">
            <div>
              <h3>Chip calendar</h3>
              <p className="hint">
                Where your squad's own blanks and doubles fall, and which gameweek each chip you have
                left best suits.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="small"
            onClick={() => requestChipPlan(squad.id)}
            disabled={chipPlanning}
            aria-busy={chipPlanning}
          >
            {chipPlanning && <span className="spinner" aria-hidden="true" />}
            {chipPlanning ? 'Planning…' : 'Plan my chips'}
          </button>

          {chipPlanProblems.length > 0 && (
            <ul className="notices mt-md">
              {chipPlanProblems.map((problem) => (
                <li key={problem} className="error">
                  {problem}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {chipPlan && <ChipCalendar plan={chipPlan} />}

      {hasSquad && importSquadPanel}
      <SquadLibrary onLoad={onLoadFromLibrary} refreshSignal={libraryRefresh} />

      {/* Account-level, not tied to any particular squad — sits at the
       * bottom of the desk regardless of hasSquad (T-069). */}
      <EmailPreferences />
    </>
  );
}
